import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";

/**
 * BulkImportWizard — 3-step CSV/XLSX import flow (BDC referral claims
 * today, any tabular upload later):
 *
 *   1. Upload — drag/drop or click (react-dropzone, 10MB cap).
 *   2. Map    — preview the first 5 rows; match headers to target
 *               fields (entity_type, target_email, target_name,
 *               notes). Sensible defaults when header names overlap.
 *   3. Confirm— POST the mapped rows to props.onSubmit(rows) and show
 *               per-row status (✓ / ✗ with error) as the caller
 *               reports back via returned { results }.
 *
 * Props:
 *   targetFields  [{ key, label, required?: bool }]
 *   onSubmit      async (rows) => { job_id } OR { results: [{row, ok, error?}] }
 *   onPollJob     optional async (jobId) => { status, processed, errors }
 *   maxRows       default 1000
 *   onClose       called when the wizard should close (back to parent)
 */

const DEFAULT_MAX = 1000;
// Job-poll safety net: cap at 60 iterations × 2s delay = 2 minutes. Beyond
// that we stop polling and surface a "check back later" message so the
// wizard can't spin forever if the backend job stalls.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ITER = 60;

function guessMapping(header, targetFields) {
  const h = String(header || "").toLowerCase().trim();
  for (const f of targetFields) {
    if (h === f.key.toLowerCase() || h === f.label.toLowerCase()) return f.key;
  }
  return null;
}

function BulkImportWizard({
  targetFields = [],
  onSubmit,
  onPollJob,
  maxRows = DEFAULT_MAX,
  onClose,
}) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onDrop = useCallback((accepted) => {
    setParseError(null);
    const f = accepted && accepted[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setParseError("File exceeds 10 MB limit.");
      return;
    }
    setFile(f);
    setParsing(true);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      preview: maxRows + 1,
      complete: (res) => {
        setParsing(false);
        if (!res || !res.data) {
          setParseError("Could not parse file.");
          return;
        }
        if (res.data.length > maxRows) {
          setParseError(`File has more than ${maxRows} data rows.`);
          return;
        }
        const h = (res.meta && res.meta.fields) || Object.keys(res.data[0] || {});
        setHeaders(h);
        setRawRows(res.data);
        const auto = {};
        for (const hdr of h) {
          const m = guessMapping(hdr, targetFields);
          if (m && !Object.values(auto).includes(m)) auto[hdr] = m;
        }
        setMapping(auto);
        setStep(2);
      },
      error: (err) => {
        setParsing(false);
        setParseError(err && err.message ? err.message : "Parse failed.");
      },
    });
  }, [maxRows, targetFields]);

  // CSV only. XLSX was accepted in an earlier iteration but the parser
  // underneath (papaparse) only handles CSV — feeding .xlsx bytes produced
  // silent garbage. Ship CSV now; add SheetJS if .xlsx is genuinely required.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const allRequiredMapped = targetFields
    .filter((f) => f.required)
    .every((f) => Object.values(mapping).includes(f.key));

  function reshape() {
    return rawRows.map((r) => {
      const out = {};
      for (const [hdr, key] of Object.entries(mapping)) {
        if (!key) continue;
        out[key] = r[hdr];
      }
      return out;
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    setResult(null);
    try {
      const rows = reshape();
      const res = await onSubmit(rows);
      if (res && res.job_id && onPollJob) {
        // Async job — poll until done, but bounded so a stuck backend
        // doesn't leave the wizard spinning forever.
        let status = "PROCESSING";
        let last = null;
        let iter = 0;
        while ((status === "PROCESSING" || status === "QUEUED") && iter < POLL_MAX_ITER) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          last = await onPollJob(res.job_id);
          status = (last && last.status) || "FAILED";
          iter += 1;
          if (last && last.processed !== undefined) {
            setResult({ inFlight: true, last });
          }
        }
        if (iter >= POLL_MAX_ITER && (status === "PROCESSING" || status === "QUEUED")) {
          setResult({
            inFlight: false,
            last: {
              status: "TIMEOUT",
              error: `Job is taking longer than expected (${(POLL_INTERVAL_MS * POLL_MAX_ITER) / 1000}s). Check the job runs page for final status.`,
            },
          });
        } else {
          setResult({ inFlight: false, last });
        }
      } else if (res && Array.isArray(res.results)) {
        setResult({ inFlight: false, last: { status: "COMPLETED", results: res.results } });
      } else {
        setResult({ inFlight: false, last: res || { status: "COMPLETED" } });
      }
      setStep(3);
    } catch (err) {
      setResult({
        inFlight: false,
        last: {
          status: "FAILED",
          error: (err && (err.response?.data?.message || err.message)) || "Submit failed.",
        },
      });
      setStep(3);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bulk-wizard" role="dialog" aria-modal="true" aria-labelledby="bulk-wizard-title">
      <h3 id="bulk-wizard-title" style={{ margin: "0 0 12px" }}>Bulk import</h3>
      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>Step {step} of 3</div>

      {step === 1 && (
        <>
          <div
            {...getRootProps()}
            style={{
              border: "2px dashed " + (isDragActive ? "#0f766e" : "#d1d5db"),
              background: isDragActive ? "#ecfdf5" : "#f9fafb",
              borderRadius: 8,
              padding: 32,
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            {parsing ? (
              <p>Parsing file…</p>
            ) : (
              <p>
                {isDragActive ? "Drop the file here" : "Drag & drop a CSV or XLSX, or click to browse"}
                <br />
                <small style={{ color: "#6b7280" }}>Max {maxRows} rows, 10 MB.</small>
              </p>
            )}
          </div>
          {parseError && (
            <div style={errStyle} role="alert">{parseError}</div>
          )}
          {onClose && (
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
            </div>
          )}
        </>
      )}

      {step === 2 && (
        <>
          <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 12 }}>
            Map source columns to target fields. Preview below shows first 5 rows.
          </p>
          <table style={{ width: "100%", fontSize: 13, marginBottom: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Source header</th>
                <th style={thStyle}>Maps to</th>
                <th style={thStyle}>Sample</th>
              </tr>
            </thead>
            <tbody>
              {headers.map((h) => (
                <tr key={h}>
                  <td style={tdStyle}><code>{h}</code></td>
                  <td style={tdStyle}>
                    <select
                      value={mapping[h] || ""}
                      onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    >
                      <option value="">— skip —</option>
                      {targetFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}{f.required ? " *" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, color: "#6b7280" }}>
                    {(rawRows.slice(0, 3).map((r) => r[h]).filter(Boolean).join(", ") || "—").slice(0, 60)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" onClick={() => setStep(1)} style={btnSecondary}>Back</button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allRequiredMapped || submitting}
              style={btnPrimary}
            >
              {submitting ? "Submitting…" : `Import ${rawRows.length} rows`}
            </button>
          </div>
        </>
      )}

      {step === 3 && result && (
        <>
          {result.inFlight ? (
            <p>Processing… {result.last?.processed ?? 0} rows so far.</p>
          ) : (
            <div>
              <p>
                Status: <strong>{result.last?.status || "COMPLETED"}</strong>
                {result.last?.processed !== undefined ? ` — ${result.last.processed} rows processed` : ""}
              </p>
              {result.last?.error && <div style={errStyle}>{result.last.error}</div>}
              {result.last?.results && (
                <ul style={{ maxHeight: 200, overflow: "auto", fontSize: 12 }}>
                  {result.last.results.slice(0, 20).map((r, i) => (
                    <li key={i}>
                      {r.ok ? "✓" : "✗"} row {i + 1}
                      {r.error ? ` — ${r.error}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {onClose && (
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button type="button" onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const btnPrimary = {
  padding: "8px 16px",
  background: "#0f766e",
  color: "#fff",
  border: "1px solid #0f766e",
  borderRadius: 5,
  cursor: "pointer",
  fontWeight: 500,
};
const btnSecondary = {
  padding: "8px 16px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 5,
  cursor: "pointer",
  fontWeight: 500,
};
const thStyle = {
  padding: "6px 10px",
  borderBottom: "1px solid #e5e7eb",
  textAlign: "left",
  background: "#f9fafb",
};
const tdStyle = {
  padding: "6px 10px",
  borderBottom: "1px solid #f3f4f6",
};
const errStyle = {
  marginTop: 12,
  padding: "10px 14px",
  background: "#fef2f2",
  color: "#b91c1c",
  border: "1px solid #fecaca",
  borderRadius: 5,
  fontSize: 14,
};

export default BulkImportWizard;
